import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Users, UserPlus, Pencil, Trash2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const PERMISSION_FIELDS = [
  { key: 'view_payments', label: 'View payments' },
  { key: 'create_payment_links', label: 'Create payment links' },
  { key: 'create_invoices', label: 'Create invoices' },
  { key: 'manage_products', label: 'Manage products' },
  { key: 'manage_orders', label: 'Manage orders' },
  { key: 'manage_customers', label: 'Manage customers' },
  { key: 'use_pos', label: 'Use POS' },
  { key: 'view_analytics', label: 'View analytics' },
  { key: 'manage_store_settings', label: 'Manage store settings' },
  { key: 'withdraw_funds', label: 'Withdraw funds' },
  { key: 'edit_gateway_settings', label: 'Edit gateway settings' },
];

const ROLE_DEFAULTS = {
  manager: {
    view_payments: true,
    create_payment_links: true,
    create_invoices: true,
    manage_products: true,
    manage_orders: true,
    manage_customers: true,
    use_pos: true,
    view_analytics: true,
    manage_store_settings: true,
    withdraw_funds: false,
    edit_gateway_settings: false,
  },
  cashier: {
    view_payments: true,
    create_payment_links: false,
    create_invoices: false,
    manage_products: false,
    manage_orders: true,
    manage_customers: true,
    use_pos: true,
    view_analytics: false,
    manage_store_settings: false,
    withdraw_funds: false,
    edit_gateway_settings: false,
  },
  viewer: {
    view_payments: true,
    create_payment_links: false,
    create_invoices: false,
    manage_products: false,
    manage_orders: false,
    manage_customers: false,
    use_pos: false,
    view_analytics: true,
    manage_store_settings: false,
    withdraw_funds: false,
    edit_gateway_settings: false,
  },
};

function buildPermissions(role, existing = {}) {
  const defaults = ROLE_DEFAULTS[role] || ROLE_DEFAULTS.viewer;
  const merged = { ...defaults, ...existing };
  return PERMISSION_FIELDS.reduce((acc, field) => {
    acc[field.key] = !!merged[field.key];
    return acc;
  }, {});
}

function permissionSummary(permissions = {}) {
  const current = permissions || {};
  return PERMISSION_FIELDS.filter((field) => current[field.key]).map((field) => field.label);
}

function initialForm(role = 'viewer', staff = null) {
  return {
    name: staff?.name || '',
    email: staff?.email || '',
    phone: staff?.phone || '',
    role: staff?.role || role,
    status: staff?.status || 'invited',
    permissions: buildPermissions(staff?.role || role, staff?.permissions || {}),
  };
}

export default function Staff() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [accessDenied, setAccessDenied] = useState(false);

  const canUseStaff = user?.premium?.can_use_staff_accounts ?? user?.can_use_staff_accounts;
  const staffLimit = user?.premium?.staff_limit ?? user?.staff_limit ?? 0;

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/dashboard/staff');
      setMembers(data.staff || []);
      setAccessDenied(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setAccessDenied(true);
      }
      toast.error(err.response?.data?.detail || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const resetInvite = () => {
    setForm(initialForm('viewer'));
  };

  const openInvite = () => {
    resetInvite();
    setEditing(null);
    setShowInvite(true);
  };

  const openEdit = (member) => {
    setEditing(member);
    setForm(initialForm(member.role, member));
    setShowInvite(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        role: form.role,
        status: form.status,
        permissions: form.permissions,
      };
      if (editing) {
        await api.put(`/dashboard/staff/${editing.id}`, payload);
        toast.success('Staff member updated');
      } else {
        await api.post('/dashboard/staff', payload);
        toast.success('Invitation sent');
      }
      setShowInvite(false);
      setEditing(null);
      resetInvite();
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save staff member');
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (key, checked) => {
    setForm((prev) => ({ ...prev, permissions: { ...prev.permissions, [key]: !!checked } }));
  };

  const handleRoleChange = (role) => {
    setForm((prev) => ({ ...prev, role, permissions: buildPermissions(role, {}) }));
  };

  const deleteStaff = async (member) => {
    if (!window.confirm(`Remove ${member.name || member.email}?`)) return;
    try {
      await api.delete(`/dashboard/staff/${member.id}`);
      toast.success('Staff member removed');
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove staff');
    }
  };

  const toggleStatus = async (member, status) => {
    try {
      await api.put(`/dashboard/staff/${member.id}`, { ...member, status });
      toast.success(`Staff member ${status}`);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const usageLabel = useMemo(() => {
    if (!staffLimit) return 'Unlimited';
    return `${members.filter((member) => ['invited', 'active'].includes(member.status)).length}/${staffLimit}`;
  }, [members, staffLimit]);

  if (canUseStaff === false || accessDenied) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="text-emerald-700 mt-0.5" size={20} />
            <div>
              <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Staff accounts</h1>
              <p className="text-sm text-slate-500 mt-1">Your current package does not include staff accounts.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Staff</h1>
          <p className="text-slate-500 text-sm mt-0.5">Invite staff and control what they can access</p>
        </div>
        <Button onClick={openInvite} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="invite-staff-btn">
          <UserPlus size={16} className="mr-1.5" /> Invite Staff
        </Button>
      </div>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editing ? 'Edit Staff Member' : 'Invite Staff Member'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} className="mt-1" placeholder="Staff name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} className="mt-1" placeholder="staff@example.com" required />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} className="mt-1" placeholder="2567XXXXXXXX" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={handleRoleChange}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="cashier">Cashier</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {editing && (
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(status) => setForm((prev) => ({ ...prev, status }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invited">Invited</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Permissions</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PERMISSION_FIELDS.map((field) => (
                  <label key={field.key} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <Checkbox checked={!!form.permissions[field.key]} onCheckedChange={(checked) => togglePermission(field.key, checked)} />
                    <span className="text-sm text-slate-700">{field.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">Staff limit usage: {usageLabel}</p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving}>
                  {saving ? 'Saving...' : (editing ? 'Save Changes' : 'Send Invitation')}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <div className="py-12 text-center">
              <Users size={40} className="mx-auto text-slate-300 mb-3" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">No staff yet</h3>
              <p className="text-sm text-slate-500 mb-4">Invite team members to help manage your business</p>
              <Button onClick={openInvite} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <UserPlus size={16} className="mr-1.5" /> Invite First Staff Member
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Staff</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Role</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Permissions</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Created</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id} className="hover:bg-slate-50/50" data-testid={`staff-row-${member.id}`}>
                      <TableCell>
                        <p className="text-sm font-medium text-slate-900">{member.name || member.email}</p>
                        <p className="text-xs text-slate-400">{member.email}</p>
                      </TableCell>
                      <TableCell><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs capitalize">{member.role}</span></TableCell>
                      <TableCell>
                        <span className={`rounded-full px-2 py-0.5 text-xs ${member.status === 'active' ? 'bg-emerald-100 text-emerald-800' : member.status === 'invited' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
                          {member.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                          {(permissionSummary(member.permissions) || []).slice(0, 4).map((label) => (
                            <span key={label} className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">{label}</span>
                          ))}
                          {permissionSummary(member.permissions).length > 4 && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">+{permissionSummary(member.permissions).length - 4}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{member.created_at ? new Date(member.created_at).toLocaleDateString() : '—'}</TableCell>
                      <TableCell className="flex gap-1">
                        {member.status === 'disabled' ? (
                          <Button variant="outline" size="sm" onClick={() => toggleStatus(member, 'active')}>Enable</Button>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => toggleStatus(member, 'disabled')}>Disable</Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => openEdit(member)}>
                          <Pencil size={14} className="mr-1" /> Edit
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteStaff(member)} className="text-red-500 hover:text-red-700">
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
